import {
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  where,
  writeBatch,
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { cadenciaParaLead, proximaData } from '@/leads/cadencia';
import { montarEventoConfirmacao, podeConfirmar } from '@/agenda/confirmacao';
import { ETAPA_AO_CANCELAR, textoCancelamento, validarCancelamento } from '@/agenda/cancelamento';
import { ehAvanco } from '@/leads/progresso';
import {
  comitarComConclusoes,
  concluirAbertasNoLote,
  lerAbertasDoLead,
  montarTarefa,
  textoInteracaoTarefa,
} from '@/services/tarefasService';
import { abertasAFechar, disparaAgendou, sufixoDaConclusao } from '@/leads/conclusaoAutomatica';
import { validarEtapa } from '@/leads/regras';
import { camposDoComparecimento } from '@/leads/comparecimento';
import { dadosDaTarefaDeRetorno } from '@/leads/retorno';
import { comResgate } from '@/leads/resgate';
import { autoria } from '@/leads/autoria';
import { registrarErro, tratarErro } from '@/lib/erros';

const colecao = (clinicaId) => collection(db, 'clinicas', clinicaId, 'agendamentos');
const colecaoTarefas = (clinicaId, leadId) =>
  collection(db, 'clinicas', clinicaId, 'leads', leadId, 'tarefas');
const refAgendamento = (clinicaId, id) => doc(db, 'clinicas', clinicaId, 'agendamentos', id);
const refLead = (clinicaId, leadId) => doc(db, 'clinicas', clinicaId, 'leads', leadId);
const colecaoInteracoes = (clinicaId, leadId) =>
  collection(db, 'clinicas', clinicaId, 'leads', leadId, 'interacoes');
const colecaoAuditoria = (clinicaId) => collection(db, 'clinicas', clinicaId, 'auditoria');

/**
 * Os quatro status GRAVADOS. A VF-3 não acrescentou um quinto: confirmação é
 * campo (`confirmadoEm`), não status — vide `agenda/confirmacao.js`.
 *
 * ★ VF-3 — A PALETA MUDOU, e em UM LUGAR SÓ.
 *
 * Verde significava "compareceu". O dono pediu verde para "confirmado" e azul
 * para "compareceu"; manter as duas convenções faria verde significar duas
 * coisas conforme a tela. A cor de exibição vive agora em `ESTADOS_VISUAIS`
 * (`agenda/confirmacao.js`), e estas aqui a acompanham para as pastilhas das
 * telas que ainda leem o status cru.
 */
export const STATUS_AGENDAMENTO = [
  { id: 'agendado', label: 'Agendado', cor: '#94a3b8' },
  { id: 'compareceu', label: 'Compareceu', cor: '#2563eb' },
  { id: 'faltou', label: 'Faltou', cor: '#dc2626' },
  { id: 'cancelado', label: 'Cancelado', cor: '#64748b' },
];

/**
 * A natureza do agendamento (VF-1).
 *
 * ⚠ Só `avaliacao` consome vaga da grade. A mesma tela marca a primeira consulta
 * e o retorno de quem já compareceu; contar os dois faria um retorno ocupar uma
 * vaga de avaliação que está livre.
 *
 * ⚠ E o tipo é GRAVADO, nunca inferido da etapa do lead depois: a natureza do
 * agendamento é o que ele era QUANDO FOI MARCADO. Derivá-la da etapa atual faria
 * o passado mudar toda vez que o lead avançasse no funil.
 */
export const TIPOS_AGENDAMENTO = [
  { id: 'avaliacao', label: 'Avaliação' },
  { id: 'retorno', label: 'Retorno' },
];
export const TIPO_PADRAO = 'avaliacao';

export const statusPorId = (id) =>
  STATUS_AGENDAMENTO.find((s) => s.id === id) ?? { id, label: id ?? '—', cor: '#94a3b8' };

// O resultado da consulta move o lead no funil (PLANO 4.3): comparecer avança,
// faltar manda para a etapa de reagendamento.
const ETAPA_POR_STATUS = {
  compareceu: 'compareceu',
  faltou: 'nao_compareceu',
};

/**
 * A menor data futura entre a que o lead já tinha e a nova.
 *
 * Uma data no passado não é "próximo agendamento": se a anterior já passou, a
 * nova manda, mesmo sendo depois.
 */
export function menorFuturo(anterior, nova, agora = new Date()) {
  const a = anterior?.toDate?.() ?? (anterior instanceof Date ? anterior : null);
  if (!a || a <= agora) return nova;
  return a < nova ? a : nova;
}

/**
 * Agendamentos de um intervalo — LEITURA ÚNICA (VF-2).
 *
 * ★ Semana e mês usam ISTO, e não `ouvirAgendamentos`. A diferença não é de
 * estilo: só o DIA precisa reagir na hora, porque é a tela onde a recepção marca
 * comparecimento e vê o resultado. Um listener aberto sobre um MÊS ficaria vivo
 * enquanto a pessoa navega, custando por uma reatividade que ninguém usa —
 * ninguém fica olhando a visão mensal esperando uma consulta mudar de status.
 */
export async function buscarAgendamentos(clinicaId, { de, ate }) {
  const q = query(
    colecao(clinicaId),
    where('dataHora', '>=', de),
    where('dataHora', '<=', ate),
    orderBy('dataHora', 'asc'),
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/** Agendamentos de um dia — LEITURA ÚNICA, para o diálogo consultar ao trocar a data. */
export async function buscarAgendamentosDoDia(clinicaId, data) {
  const { inicio, fim } = limitesDoDia(data);
  const q = query(
    colecao(clinicaId),
    where('dataHora', '>=', inicio),
    where('dataHora', '<=', fim),
    orderBy('dataHora', 'asc'),
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

const formatar = (data) =>
  data.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });

/** Agendamentos num intervalo, em ordem cronológica. */
export function ouvirAgendamentos(clinicaId, { de, ate } = {}, aoReceber, aoFalhar) {
  const restricoes = [];
  if (de) restricoes.push(where('dataHora', '>=', de));
  if (ate) restricoes.push(where('dataHora', '<=', ate));

  const q = query(colecao(clinicaId), ...restricoes, orderBy('dataHora', 'asc'));
  return onSnapshot(
    q,
    (snap) => aoReceber(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
    tratarErro('os agendamentos', aoFalhar),
  );
}

/**
 * ★★ WO lote 3a — OS AGENDAMENTOS PENDENTES: vencidos e sem baixa.
 *
 * ★ Consulta ESTREITA de propósito: `status == 'agendado'` + `dataHora <= agora` devolve
 * **757** documentos, contra os **2.658** da coleção inteira. ⚠ O selo do card aparece no
 * funil, que é a tela mais aberta da casa — baixar a coleção toda para acender um selo
 * seria caro todo dia por um detalhe.
 *
 * ★ O índice já existe: `agendamentos (status ASC, dataHora ASC)`, escopo COLLECTION.
 * ⚠ Nenhum índice novo — conferido no `firestore.indexes.json`.
 *
 * ⚠ `agora` é fixado no momento da assinatura: um agendamento que vence com a aba aberta
 * só entra na próxima carga. ★ É aceitável e está dito — alternativa seria reassinar a cada
 * minuto, caro e sem ganho para quem olha um funil.
 */
export function ouvirPendentes(clinicaId, aoReceber, aoFalhar, agora = new Date()) {
  const q = query(colecao(clinicaId), where('status', '==', 'agendado'), where('dataHora', '<=', agora));
  return onSnapshot(
    q,
    (snap) => aoReceber(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
    tratarErro('os agendamentos pendentes', aoFalhar),
  );
}

export function ouvirAgendamentosDoLead(clinicaId, leadId, aoReceber, aoFalhar) {
  const q = query(colecao(clinicaId), where('leadId', '==', leadId), orderBy('dataHora', 'desc'));
  return onSnapshot(
    q,
    (snap) => aoReceber(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
    tratarErro('os agendamentos do lead', aoFalhar),
  );
}

/** As consultas de um lead — LEITURA ÚNICA, para o diálogo de cancelar decidir. */
export async function buscarAgendamentosDoLead(clinicaId, leadId) {
  const q = query(colecao(clinicaId), where('leadId', '==', leadId), orderBy('dataHora', 'desc'));
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/**
 * Cancela a consulta, liberando a vaga (VF-3, correção 3).
 *
 * ★ `confirmadoEm` NÃO É APAGADO. "Confirmou e cancelou" é fato, e vale
 * guardar. A regra da VF-3 é que só REMARCAR apaga o carimbo — porque só
 * remarcar muda a data a que a confirmação se referia.
 *
 * ★ A ETAPA só muda se quem cancelou disser. `voltarEtapa` vem do diálogo, e o
 * diálogo só pergunta quando esta era a ÚLTIMA consulta futura: manter o lead em
 * `agendado` sem consulta nenhuma é a mentira no funil que o projeto já carrega
 * como pendência, e não vamos produzi-la de propósito numa porta nova.
 *
 * ⚠ ESCREVE NO LEAD (interação, e a etapa quando muda). As rules reservam
 * `leads` e `leads/{id}/interacoes` a `podeVendas` — então a RECEPÇÃO não pode
 * cancelar, pelo mesmo motivo que não marca comparecimento. O botão não é
 * oferecido a ela; habilitá-la exigiria uma callable com Admin SDK. Nenhuma
 * regra foi afrouxada.
 */
export async function cancelarAgendamento(clinicaId, agendamento, opcoes = {}, autor) {
  const motivo = String(opcoes.motivo ?? '').trim();
  const voltarEtapa = opcoes.voltarEtapa === true;
  const retornoEm = opcoes.retornoEm instanceof Date ? opcoes.retornoEm : null;

  /**
   * ★ VQ-3 — cancelar a ÚLTIMA consulta futura move o lead para NÃO COMPARECEU
   * (`ETAPA_AO_CANCELAR`), exigindo a data de retorno (tarefa no mesmo lote,
   * como a VJ). O agendamento fica `cancelado` — NUNCA `faltou`: a taxa de
   * comparecimento (compareceu ÷ marcados) não muda por um cancelamento. "Não
   * compareceu" não tem motivo (VG). A regra do funil continua valendo por cima.
   */
  if (voltarEtapa && agendamento.leadId) {
    const erro = validarCancelamento({ voltarEtapa, retornoEm }) ?? validarEtapa(ETAPA_AO_CANCELAR, { retornoEm });
    if (erro) throw new Error(erro);
  }

  /**
   * ★★ PC-2 (#3) — a tarefa de retorno do "voltar etapa" ENCERRA as abertas do mesmo
   * responsável. ⚠ O lead aqui é o mínimo que o agendamento desnormaliza (sem SDR), então o
   * "mesmo responsável" é o da tarefa nova, ou quem cancelou; aberta sem `responsavelId` não
   * fecha — não dá para resolver o dono dela sem ler o lead. Leitura ANTES do lote.
   */
  /**
   * ★★ Nome do lead (28/out) — a tarefa de retorno leva o nome ATUAL do lead, não a cópia do agendamento,
   * que pode ser de antes de uma renomeação: sem isto o nome velho passava adiante para a tarefa nova. Lê
   * o lead só quando a tarefa vai nascer (`voltarEtapa`); cancelar já é de vendas, que lê `leads`.
   */
  let nomeAtual = agendamento.leadNome ?? '';
  if (agendamento.leadId && voltarEtapa) {
    const lido = opcoes.lead
      ?? (await getDoc(refLead(clinicaId, agendamento.leadId)).then((s) => (s.exists() ? s.data() : null)));
    if (String(lido?.nome ?? '').trim()) nomeAtual = lido.nome;
  }
  const leadMinimo = agendamento.leadId
    ? { id: agendamento.leadId, nome: nomeAtual, telefone: agendamento.leadTelefone ?? '' }
    : null;
  let tarefaNova = null;
  let refTarefaNova = null;
  let aFechar = [];
  if (agendamento.leadId && voltarEtapa) {
    // A data de retorno vira tarefa no mesmo lote. O lead aqui é só o que o
    // agendamento desnormaliza (sem SDR).
    // ⚠ MEDIDO na PC-2 (E8): a tarefa nasce com `responsavelId: ''`, NÃO com quem cancelou —
    // `dadosDaTarefaDeRetorno` devolve '' e o `??` de `montarTarefa` não pula string vazia.
    // A fila herda o SDR do lead na leitura (`responsavelDaTarefa`). Defeito anterior à PC-2,
    // registrado em `docs/pc2-testes.md`; comportamento não mudado aqui.
    tarefaNova = montarTarefa(clinicaId, leadMinimo, dadosDaTarefaDeRetorno(leadMinimo, {
      etapa: ETAPA_AO_CANCELAR,
      motivoRotulo: 'Consulta cancelada',
      retornoEm,
    }), autor);
    refTarefaNova = doc(colecaoTarefas(clinicaId, agendamento.leadId));
    const { abertas } = await lerAbertasDoLead(clinicaId, agendamento.leadId);
    aFechar = abertasAFechar(abertas, {
      motivo: 'novo_contato',
      responsavelNovo: tarefaNova.responsavelId || autor?.id || '',
      excetoIds: [refTarefaNova.id],
    });
  }

  const montar = (fechar) => {
    const lote = writeBatch(db);
    lote.update(refAgendamento(clinicaId, agendamento.id), {
      status: 'cancelado',
      canceladoMotivo: motivo,
      atualizadoEm: serverTimestamp(),
      resolvidoPorId: autor?.id ?? 'sistema',
      resolvidoPorNome: autor?.nome ?? 'Sistema',
    });

    if (agendamento.leadId) {
      if (voltarEtapa) {
        // VQ-3: sem motivo (Não compareceu não tem); `ultimoCancelamentoEm` é o que
        // deixa o card/ficha dizer "cancelou" em vez de parecer falta.
        lote.update(refLead(clinicaId, agendamento.leadId), {
          etapa: ETAPA_AO_CANCELAR,
          ultimoCancelamentoEm: serverTimestamp(),
          atualizadoEm: serverTimestamp(),
        });
        lote.set(refTarefaNova, tarefaNova);
        concluirAbertasNoLote(lote, clinicaId, agendamento.leadId, fechar, 'novo_contato', autor);
      }
      const retornoTexto = voltarEtapa && retornoEm
        ? ` Retorno em ${retornoEm.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })}.`
        : '';
      lote.set(doc(colecaoInteracoes(clinicaId, agendamento.leadId)), {
        tipo: voltarEtapa ? 'mudanca_etapa' : 'nota',
        texto: textoCancelamento(agendamento, motivo, formatar) + retornoTexto + sufixoDaConclusao(fechar.length),
        ...(voltarEtapa ? { para: ETAPA_AO_CANCELAR } : {}),
        ...autoria(autor, { clinicaId, carimbo: serverTimestamp }),
      });
    }
    return lote;
  };

  await comitarComConclusoes(montar, aFechar);
}

/**
 * Cria o agendamento e move o lead para "agendado" no mesmo lote — o funil não
 * pode dizer que o lead está em contato enquanto existe consulta marcada.
 */
export async function criarAgendamento(clinicaId, lead, dados, autor) {
  const dataHora = dados.dataHora instanceof Date ? dados.dataHora : new Date(dados.dataHora);
  if (Number.isNaN(dataHora.getTime())) {
    throw new Error('Informe uma data e hora válidas para a consulta.');
  }

  const agendamento = {
    leadId: lead.id,
    leadNome: lead.nome ?? '',
    leadTelefone: lead.telefone ?? '',
    dataHora,
    profissional: String(dados.profissional ?? '').trim(),
    unidade: String(dados.unidade ?? '').trim(),
    procedimento: String(dados.procedimento ?? lead.procedimento ?? '').trim(),
    observacoes: String(dados.observacoes ?? '').trim(),
    status: 'agendado',
    // VF-1: `avaliacao` consome vaga da grade; `retorno` não.
    tipo: TIPOS_AGENDAMENTO.some((t) => t.id === dados.tipo) ? dados.tipo : TIPO_PADRAO,
    // Marcado fora dos horários da grade, com o registro na timeline. Existe
    // porque um caso real que não cabe precisa caber em algum lugar — sem ele,
    // a equipe voltaria a criar lead agendado sem consulta.
    foraDaGrade: dados.foraDaGrade === true,
    // Prepara o terreno para a Fase 4: a IA também marca consultas.
    agendadoPor: dados.agendadoPor ?? 'humano',
    agendadoPorId: autor?.id ?? 'sistema',
    agendadoPorNome: autor?.nome ?? 'Sistema',
    clinicaId,
    criadoEm: serverTimestamp(),
    atualizadoEm: serverTimestamp(),
  };

  const ref = doc(colecao(clinicaId));
  /**
   * ★★ PC-2 (A1) — AGENDAR ENCERRA TODAS AS ABERTAS DO LEAD, mas só na TRANSIÇÃO.
   *
   * ⚠ O update abaixo grava `etapa: 'agendado'` mesmo quando já estava; a decisão olha
   * `lead.etapa`, a MESMA que a interação grava como `de`. 2ª consulta (lead já agendado)
   * não lê nada e não fecha nada — o lembrete criado depois de agendar sobrevive (E11).
   */
  const aFechar = disparaAgendou(lead.etapa)
    ? abertasAFechar((await lerAbertasDoLead(clinicaId, lead.id)).abertas, { motivo: 'agendou' })
    : [];
  const montar = (fechar) => {
    const lote = writeBatch(db);
    lote.set(ref, agendamento);
    lote.update(refLead(clinicaId, lead.id), {
      etapa: 'agendado',
      // VR: agendou em mês posterior ao de criadoEm → resgate, no MESMO lote (data da ação = agora).
      ...comResgate(lead, new Date()),
      // ★ O MENOR futuro, não o último marcado. Antes gravava a data recém-criada
      // sem comparar: marcar 20/set depois de já haver 10/set fazia o campo dizer
      // 20/set. O campo é escrito em dois lugares e lido em nenhum — era dado
      // morto que mentia, e um campo que mente é armadilha para quem for lê-lo.
      proximoAgendamento: menorFuturo(lead.proximoAgendamento, dataHora),
      atualizadoEm: serverTimestamp(),
    });
    lote.set(doc(colecaoInteracoes(clinicaId, lead.id)), {
      tipo: 'mudanca_etapa',
      texto: `Consulta agendada para ${formatar(dataHora)}${
        agendamento.profissional ? ` com ${agendamento.profissional}` : ''
      }.${sufixoDaConclusao(fechar.length)}`,
      de: lead.etapa,
      para: 'agendado',
      ...autoria(autor, { clinicaId, lead, carimbo: serverTimestamp }),
    });
    concluirAbertasNoLote(lote, clinicaId, lead.id, fechar, 'agendou', autor);
    return lote;
  };
  await comitarComConclusoes(montar, aFechar);

  return ref.id;
}

/**
 * Registra o desfecho da consulta e leva o lead junto: compareceu avança no
 * funil, faltou vai para reagendamento. Cancelar não mexe na etapa — quem
 * cancela decide o que fazer com o lead.
 */
/** Título e tipo da tarefa automática de retorno (V4). */
export const TAREFA_RETORNO = { titulo: 'Retorno pós-avaliação', tipo: 'retorno', origem: 'comparecimento' };

/**
 * Já existe retorno pendente para este lead? Duas igualdades, sem índice
 * composto. É o dedup: comparecimento repetido (dois agendamentos, clique
 * duplo) não pode empilhar tarefas iguais na fila.
 */
async function temRetornoPendente(clinicaId, leadId) {
  const snap = await getDocs(
    query(
      colecaoTarefas(clinicaId, leadId),
      where('concluida', '==', false),
      where('tipo', '==', TAREFA_RETORNO.tipo),
    ),
  );
  return !snap.empty;
}

/**
 * Registra o desfecho da consulta e leva o lead junto: compareceu avança no
 * funil, faltou vai para reagendamento. Cancelar não mexe na etapa — quem
 * cancela decide o que fazer com o lead.
 *
 * V4 — COMPARECEU também cria, NO MESMO LOTE, a tarefa "Retorno pós-avaliação"
 * (tipo `retorno`), com data pela cadência pós-comparecimento e responsável
 * `negociadorId ?? sdrId`. Quem compareceu e não fechou entra na fila de
 * Próximos Contatos sozinho — é a garantia de que não some.
 *
 * `opcoes.lead` e `opcoes.config`: quem já tem o lead e a config da clínica em
 * memória (BlocoAgendamento, dentro do LeadsProvider) passa e poupa duas
 * leituras; a Agenda, fora do provider, não passa e o serviço lê. Sem
 * negociador NÃO bloqueia (decisão do dono): a tarefa vai para o SDR e o painel
 * cobra a escolha.
 */
export async function registrarComparecimento(clinicaId, agendamento, status, autor, opcoes = {}) {
  if (!STATUS_AGENDAMENTO.some((s) => s.id === status)) {
    throw new Error(`Status inválido: ${status}.`);
  }

  // Leituras ANTES do lote: o lote é só escrita.
  let tarefaRetorno = null;
  let lead = null;
  const novaEtapa = ETAPA_POR_STATUS[status];

  /**
   * ★★ WE (b) — O LEAD É LIDO SEMPRE QUE A BAIXA MOVE ETAPA, não só no `compareceu`.
   *
   * Antes a leitura existia só no ramo `compareceu`, e só para montar a tarefa de retorno. A trava do
   * retrocesso precisa da ETAPA ATUAL nos dois ramos, e sem ela `faltou` empurraria para
   * `nao_compareceu` um lead que já compareceu (medido: acontecia).
   *
   * Custo: **um `getDoc` a mais**, e só onde o chamador não tem o lead em mãos — na prática, a AgendaPage
   * dando "Faltou". A ficha (`BlocoAgendamento`) passa `opcoes.lead` nos dois botões e não lê nada.
   */
  if (novaEtapa && agendamento.leadId) {
    lead =
      opcoes.lead ??
      (await getDoc(refLead(clinicaId, agendamento.leadId)).then((s) =>
        s.exists() ? { id: s.id, ...s.data() } : null,
      ));
  }

  /**
   * ★★ WI (29/set/2026) — A BAIXA "FALTOU" EXIGE O PRÓXIMO CONTATO.
   *
   * Palavras do dono: *"quando a gente colocar que a pessoa faltou, a gente tem que colocar
   * para gerar um novo contato, obrigatoriamente"* — e o porquê: *"senão vai ficar cliente
   * perdido"*. Medido (`docs/wi-medicao.md` §3): das 13 baixas "faltou" feitas por uma
   * pessoa, **10** deixaram o lead sem nenhuma tarefa aberta; **7 de 9** leads distintos
   * ficaram sem tarefa E sem consulta futura — perdidos de verdade.
   *
   * ★ A exigência vale SÓ quando a etapa vai mesmo se mover (`ehAvanco`). Lead que já está
   * adiante não muda de etapa, então não há próximo contato a exigir — e cobrar a data ali
   * seria cobrar por nada. A verificação é a MESMA do funil (`validarEtapa`), e roda ANTES
   * do lote: recusa não escreve.
   *
   * ★★ E O LIMITE, dito de frente: isto NÃO pode ser imposto pelas rules. A tarefa é um
   * documento IRMÃO no mesmo lote, e as rules avaliam cada escrita isoladamente, não
   * consultam e não veem o lote. Fica no cliente, como a exigência do negociador já é.
   * Fechar de verdade exigiria uma callable — deploy de functions, hoje travado.
   */
  const vaiMoverEtapa = novaEtapa && agendamento.leadId && ehAvanco(lead?.etapa, novaEtapa);
  let tarefaDoRetorno = null;
  let abertas = [];
  if (status === 'faltou' && vaiMoverEtapa) {
    const retornoEm = opcoes.retornoEm instanceof Date ? opcoes.retornoEm : null;
    const erro = validarEtapa(novaEtapa, { ...lead, retornoEm });
    if (erro) throw new Error(erro);
    tarefaDoRetorno = montarTarefa(
      clinicaId,
      lead ?? { id: agendamento.leadId, nome: agendamento.leadNome ?? '', telefone: agendamento.leadTelefone ?? '' },
      dadosDaTarefaDeRetorno(lead ?? { id: agendamento.leadId }, {
        etapa: novaEtapa,
        motivoRotulo: 'Faltou à consulta',
        retornoEm,
      }),
      autor,
    );
    // ★★ PC-2 (#4) — a tarefa do "faltou" encerra as abertas do MESMO responsável.
    abertas = (await lerAbertasDoLead(clinicaId, agendamento.leadId)).abertas;
  }

  if (status === 'compareceu' && agendamento.leadId && lead) {
    /**
     * ★★ PC-2 (#5) — UMA leitura serve às duas coisas: o dedup do retorno e as abertas a
     * encerrar. ⚠ Se a leitura do servidor falhar, o dedup cai no `temRetornoPendente` de
     * antes — a limpeza pode ficar para depois; tarefa duplicada na fila, não.
     */
    const leitura = await lerAbertasDoLead(clinicaId, lead.id);
    abertas = leitura.abertas;
    const jaTemRetorno = leitura.falhou
      ? await temRetornoPendente(clinicaId, lead.id)
      : abertas.some((t) => t?.tipo === TAREFA_RETORNO.tipo);
    if (!jaTemRetorno) {
      const config =
        opcoes.config ??
        (await getDoc(doc(db, 'clinicas', clinicaId)).then((s) => s.data()?.config ?? null));
      const cadencia = cadenciaParaLead({ ...lead, etapa: 'compareceu' }, config);
      const responsavelId = lead.negociadorId || lead.sdrId || autor?.id || '';
      const responsavelNome = lead.negociadorId
        ? (lead.negociadorNome ?? '')
        : (lead.sdrNome ?? autor?.nome ?? '');
      tarefaRetorno = montarTarefa(
        clinicaId,
        lead,
        {
          ...TAREFA_RETORNO,
          dataHora: proximaData(0, cadencia),
          responsavelId,
          responsavelNome,
          leadEtapa: 'compareceu',
        },
        autor,
      );
    }
  }

  /**
   * ★ WY — para a mensagem de erro: o negociador que ESTE update vai deixar no documento.
   * ⚠ É o que a regra olha (`request.resource.data`), então é o que a frase pode afirmar.
   */
  const negociadorFinal = opcoes.dados?.negociadorId ?? lead?.negociadorId ?? '';
  const temNegociador = typeof negociadorFinal === 'string' && negociadorFinal.length > 0;

  /**
   * ★★ PC-2 (#4/#5) — as tarefas novas têm o id gerado AQUI, fora de `montar`: é o que vai
   * em `excetoIds` e o que a repetição de `comitarComConclusoes` reusa.
   */
  const tarefaNovaDoGesto = tarefaDoRetorno ?? tarefaRetorno;
  const refTarefaDoRetorno = tarefaDoRetorno ? doc(colecaoTarefas(clinicaId, agendamento.leadId)) : null;
  const refTarefaRetorno = tarefaRetorno ? doc(colecaoTarefas(clinicaId, agendamento.leadId)) : null;
  const aFechar = tarefaNovaDoGesto
    ? abertasAFechar(abertas, {
      motivo: 'novo_contato',
      responsavelNovo: tarefaNovaDoGesto.responsavelId,
      sdrDoLead: lead?.sdrId,
      excetoIds: [refTarefaDoRetorno?.id, refTarefaRetorno?.id].filter(Boolean),
    })
    : [];

  const montar = (fechar) => {
    const lote = writeBatch(db);

    /**
     * ★ WI — a tarefa do "faltou" entra no MESMO lote da etapa: ou as duas coisas acontecem,
     * ou nenhuma. Uma tarefa gravada fora do lote deixaria o lead em `nao_compareceu` sem
     * próximo contato se a segunda escrita falhasse — que é o estado que a peça veio extinguir.
     *
     * ★ Sem dedup, de propósito, e é a MESMA decisão da VJ no funil: *"se o lead já tinha
     * tarefa pendente, esta entra assim mesmo — a data escolhida agora é a que vale"*. O dedup
     * existe só para a tarefa AUTOMÁTICA do comparecimento, que ninguém pediu.
     */
    if (tarefaDoRetorno) {
      lote.set(refTarefaDoRetorno, tarefaDoRetorno);
      lote.set(doc(colecaoInteracoes(clinicaId, agendamento.leadId)), {
        tipo: 'tarefa',
        texto: textoInteracaoTarefa(tarefaDoRetorno),
        ...autoria(null, { clinicaId, lead, carimbo: serverTimestamp }),
      });
    }

    if (tarefaRetorno) {
      lote.set(refTarefaRetorno, tarefaRetorno);
      lote.set(doc(colecaoInteracoes(clinicaId, agendamento.leadId)), {
        tipo: 'tarefa',
        texto: textoInteracaoTarefa(tarefaRetorno),
        ...autoria(null, { clinicaId, carimbo: serverTimestamp }),
      });
    }
    lote.update(refAgendamento(clinicaId, agendamento.id), {
      status,
      atualizadoEm: serverTimestamp(),
      resolvidoPorId: autor?.id ?? 'sistema',
      resolvidoPorNome: autor?.nome ?? 'Sistema',
    });

    /**
     * ★★ WE (b) — A BAIXA PODE AVANÇAR A ETAPA, NUNCA RETROCEDER.
     *
     * Medido em produção (`docs/we-medicao.md` §3.1): das 8 baixas feitas entre 20 e 26/set, **5 fizeram a
     * etapa andar para trás** — duas desfizeram um FECHAMENTO. O caminho é sempre o mesmo e é o trabalho
     * normal da equipe: fecha-se a etapa do lead primeiro, e só depois lembra-se da consulta aberta.
     *
     * A ordem vem de `@/leads/progresso`, uma lista DECLARADA — nunca de `ETAPAS_KANBAN`, que é ordem de
     * desenho das colunas. O porquê está escrito lá, e há guarda de fonte.
     *
     * ★ A trava é da ETAPA, não da baixa: a consulta é fechada de qualquer jeito (é o gesto que a pessoa
     * pediu). O que não acontece é o lead andar para trás junto.
     */
    const moveEtapa = vaiMoverEtapa; // ★ WI: a mesma conta, feita uma vez (o 'faltou' já precisou dela)
    if (moveEtapa) {
      lote.update(refLead(clinicaId, agendamento.leadId), {
        etapa: novaEtapa,
        // VR: compareceu em mês posterior ao de criadoEm → resgate (data da ação = a consulta). Faltou nunca.
        ...(status === 'compareceu' && lead ? comResgate(lead, agendamento.dataHora?.toDate ? agendamento.dataHora.toDate() : agendamento.dataHora) : null),
        atualizadoEm: serverTimestamp(),
        // VE: o carimbo `compareceuEm` entra no MESMO lote que move a etapa, e NÃO retrocede
        // — vale o PRIMEIRO comparecimento. Quem o calcula agora é `camposDoComparecimento`.
        // ★ WI: a frase que estava aqui ("este caminho NÃO pede orçamento, o badge cobra
        // depois") DEIXOU DE SER VERDADE — a baixa passou a ter diálogo e a pedir código,
        // nome e valor, como o funil. Apagada de propósito: comentário que mente é armadilha.
        /**
         * ★★ WI (29/set/2026) — OS DADOS DO COMPARECIMENTO ENTRAM AQUI, NO MESMO LOTE.
         *
         * A WH deixava a baixa entrar em `compareceu` INCOMPLETA e marcava a dívida com
         * `comparecimentoIncompleto`, destrancada nas rules por um passe de FORMA
         * (`formaDaBaixaDaAgenda`). A WI mediu que **a recepção nunca passou por ali** — o
         * passe estava ANDado com `podeVendas`, então só servia a SDR e gestor, que é
         * justamente quem agora preenche. Sem razão para existir, o passe e a marca saíram, e
         * **a regra voltou a ser UMA SÓ**: quem entra em `compareceu` traz código, nome e
         * valor (ou a caixa "sem orçamento"), venha do funil, da agenda ou da ficha.
         *
         * ★ Os campos são calculados por `camposDoComparecimento` — o MESMO módulo puro que
         * `moverEtapa` usa. Copiar estava proibido: a limpeza da marca `semOrcamento`,
         * divergindo, faria o contador das Metas mentir para sempre.
         *
         * ★ `opcoes.dados` é opcional: a baixa "faltou" não coleta nada, e sem dados a função
         * devolve só `compareceuEm` — que é o comportamento de antes para esse caminho.
         */
        ...camposDoComparecimento(lead, opcoes.dados ?? {}, {
          novaEtapa,
          autor,
          carimbo: serverTimestamp,
        }),
      });
    }

    if (agendamento.leadId) {
      lote.set(doc(colecaoInteracoes(clinicaId, agendamento.leadId)), {
        tipo: moveEtapa ? 'mudanca_etapa' : 'nota',
        texto: `Consulta de ${formatar(
          agendamento.dataHora.toDate ? agendamento.dataHora.toDate() : agendamento.dataHora,
        )}: ${statusPorId(status).label.toLowerCase()}.${sufixoDaConclusao(fechar.length)}`,
        ...(moveEtapa ? { para: novaEtapa } : {}),
        ...autoria(autor, { clinicaId, carimbo: serverTimestamp }),
      });
    }
    if (agendamento.leadId) {
      concluirAbertasNoLote(lote, clinicaId, agendamento.leadId, fechar, 'novo_contato', autor);
    }
    return lote;
  };

  /**
   * ★★★ WY — A RECUSA DO FIRESTORE PASSA A FALAR PORTUGUÊS, E A DEIXAR RASTRO.
   *
   * ⚠⚠⚠ Dois achados da medição de 09/out, e os dois doeram:
   *
   * 1. ★ A pessoa via **"Missing or insufficient permissions"** — ⚠ em inglês, e sem dizer o
   *    que fazer. Foi assim que o defeito chegou ao dono: por uma frase que não ensina nada.
   * 2. ★★★ **ZERO `permission-denied` na tela de Saúde.** ⚠⚠ Dos 38 registros, **todos** eram
   *    de callable (`functions/...`): ★ este caminho escreve **direto no Firestore**, e
   *    **nunca passou por `tratarErro`**. ★★ É a mesma família do achado da WT lote 1 (*"87
   *    `console.error` morrendo na máquina de quem usou"*) — a WT cobriu os **listeners**, e
   *    as **escritas** ficaram de fora. ⚠ Resultado: o defeito ficou **3 dias** em produção e
   *    **não há como contar** quantas tentativas falharam.
   *
   * ★★ Por que AQUI e não em cada tela: ⚠ são **quatro** chamadores (`AgendaPage` ×2,
   * `BlocoAgendamento` ×2). ★★★ Tratar em cada um garante o quinto sem tratamento — a lição
   * do `tratarErro`: *"se o registro dependesse de quem chama, o décimo segundo `() => {}`
   * voltaria a não registrar nada"*.
   *
   * ⚠ **O que vai para a Saúde: SÓ o código e a forma.** Nada de paciente, nada de lead —
   * nem o id, que no caso da conversa já se mostrou carregar telefone.
   */
  try {
    await comitarComConclusoes(montar, aFechar);
  } catch (falha) {
    const negado = falha?.code === 'permission-denied';
    if (negado) {
      registrarErro({
        origem: 'service',
        codigo: 'baixa-consulta:permissao',
        mensagem: `Baixa ${status} recusada ao gravar o lead (faltaNegociador=${!temNegociador})`,
      });
      /**
       * ★★ A tradução. ⚠ Ela só afirma "falta o negociador" quando **de fato** falta — senão
       * mandaria a pessoa procurar um campo que está preenchido. ★ Nos outros casos diz o que
       * se sabe (a gravação foi recusada) e manda avisar o suporte, que é honesto.
       */
      throw new Error(
        temNegociador
          ? 'Não foi possível registrar: a gravação foi recusada. Avise o suporte.'
          : 'Não foi possível registrar: falta o negociador.',
      );
    }
    throw falha;
  }
}

/**
 * Liga e desliga a confirmação do paciente (VF-3).
 *
 * ★ ESCREVE SÓ O AGENDAMENTO E A AUDITORIA — NUNCA O LEAD.
 *
 * É isso que permite à RECEPÇÃO usar o flag. As rules dão a ela escrita em
 * `agendamentos` (`podeVendas || podeRecepcao`) e em `auditoria`
 * (`create: if membro`), mas NÃO em `leads` nem em `leads/{id}/interacoes`, que
 * são `podeVendas`. `registrarComparecimento` escreve no lead sempre que há
 * `leadId` — e é exatamente por isso que a recepção cai em `permission-denied`
 * ao marcar comparecimento. Este caminho não passa por lá.
 *
 * Nenhuma regra foi afrouxada para isto funcionar, e nenhuma precisa ser.
 *
 * ⚠ A escrita é deliberadamente inofensiva: não move o funil, não cria tarefa,
 * não carimba `compareceuEm`, não entra em métrica nem em conta de dinheiro. É
 * o que autoriza o flag a ser um toque só, sem diálogo de confirmação: um toque
 * errado custa um segundo toque, não um dado corrompido.
 */
export async function alternarConfirmacao(clinicaId, agendamento, marcar, autor) {
  if (!podeConfirmar(agendamento)) {
    throw new Error('Só uma consulta agendada pode ser confirmada.');
  }

  const lote = writeBatch(db);
  lote.update(refAgendamento(clinicaId, agendamento.id), {
    confirmadoEm: marcar ? serverTimestamp() : null,
    confirmadoPorId: marcar ? (autor?.id ?? 'sistema') : null,
    confirmadoPorNome: marcar ? (autor?.nome ?? 'Sistema') : null,
    confirmacaoMudadaEm: serverTimestamp(),
    atualizadoEm: serverTimestamp(),
  });
  // O documento guarda o ESTADO (quem marcou, quando); a auditoria guarda o
  // HISTÓRICO de cada toque, e é append-only nas rules.
  lote.set(doc(colecaoAuditoria(clinicaId)), {
    ...montarEventoConfirmacao(clinicaId, agendamento, marcar, autor),
    criadoEm: serverTimestamp(),
  });
  await lote.commit();
}

/**
 * Remarca a consulta.
 *
 * ★★ PC-2 (A2, decisão P1 do dono) — remarcar com o lead FORA de `agendado` (voltando de
 * "Não compareceu", por exemplo) é TRANSIÇÃO e encerra as abertas, como agendar. Com o lead
 * já agendado não lê tarefa nenhuma e não fecha nada (E3/E11).
 *
 * `opcoes.lead`: quem tem o lead em mãos (ficha, diálogo de etapa) passa e poupa a leitura;
 * a Agenda, fora do provider, não passa e o serviço lê (1 `getDoc`).
 */
export async function reagendar(clinicaId, agendamento, novaData, autor, opcoes = {}) {
  const data = novaData instanceof Date ? novaData : new Date(novaData);
  if (Number.isNaN(data.getTime())) throw new Error('Data inválida para reagendar.');

  let aFechar = [];
  if (agendamento.leadId) {
    const lead =
      opcoes.lead ??
      (await getDoc(refLead(clinicaId, agendamento.leadId)).then((s) =>
        s.exists() ? { id: s.id, ...s.data() } : null,
      ));
    if (lead && disparaAgendou(lead.etapa)) {
      aFechar = abertasAFechar((await lerAbertasDoLead(clinicaId, agendamento.leadId)).abertas, { motivo: 'agendou' });
    }
  }

  const montar = (fechar) => {
    const lote = writeBatch(db);
    lote.update(refAgendamento(clinicaId, agendamento.id), {
      dataHora: data,
      status: 'agendado',
      // ★ VF-3 — REAGENDAR APAGA A CONFIRMAÇÃO.
      //
      // A confirmação era daquela data. Quem confirmou terça e foi movido para
      // quinta NÃO confirmou quinta — manter o carimbo faria a grade pintar de
      // verde uma data que ninguém confirmou, e a recepção deixaria de ligar
      // justamente para quem precisa. O carimbo morre com a data que o gerou.
      confirmadoEm: null,
      confirmadoPorId: null,
      confirmadoPorNome: null,
      atualizadoEm: serverTimestamp(),
    });
    if (agendamento.leadId) {
      lote.update(refLead(clinicaId, agendamento.leadId), {
        etapa: 'agendado',
        // Ao reagendar, a data nova SUBSTITUI — a antiga deixou de existir.
        proximoAgendamento: data,
        atualizadoEm: serverTimestamp(),
      });
      lote.set(doc(colecaoInteracoes(clinicaId, agendamento.leadId)), {
        tipo: 'nota',
        texto: `Consulta reagendada para ${formatar(data)}.${sufixoDaConclusao(fechar.length)}`,
        ...autoria(autor, { clinicaId, carimbo: serverTimestamp }),
      });
      concluirAbertasNoLote(lote, clinicaId, agendamento.leadId, fechar, 'agendou', autor);
    }
    return lote;
  };
  await comitarComConclusoes(montar, aFechar);
}

/** Recorte de um dia — usado pela agenda e pelos indicadores diários. */
export function limitesDoDia(data = new Date()) {
  const inicio = new Date(data);
  inicio.setHours(0, 0, 0, 0);
  const fim = new Date(data);
  fim.setHours(23, 59, 59, 999);
  return { inicio, fim };
}
